import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Plus, X } from 'lucide-react';
import { IconTruck } from '@tabler/icons-react';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import '../../board/board-search-filters/BoardSearchFilters.css';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../process-config/ConfigStatusView2.css';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import './PermissoesCustomersView.css';
import './TransportadorasCustomersView.css';

const TIPO_POPOVER_CLASS =
  'board-search-filters-popover board-search-filters-popover--simple board-search-filters-popover--tecnico ec-module-popover transsp-tipo-popover';

function TransspTipoChipIcon({ size = 13, stroke = 1.5 }) {
  return <ChevronDown size={size} strokeWidth={stroke} aria-hidden />;
}

async function apiVc(path, { method = 'GET', body, signal } = {}) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const res = await fetch(`/api/vc-usr${path}`, {
    method,
    headers,
    signal,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json;
}

const TIPO_OPTIONS = [
  { value: 1, label: 'Transporte externo (rastreável)' },
  { value: 2, label: 'Transporte externo (não rastreável)' },
  { value: 3, label: 'Transporte interno (rastreável)' },
  { value: 4, label: 'Transporte interno (não rastreável)' },
];

function onlyDigits(v) {
  return String(v ?? '').replace(/\D/g, '');
}

function maskCpfCnpjDisplay(raw) {
  const d = onlyDigits(raw);
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  const s = String(raw ?? '').trim();
  return s || '—';
}

const TIPO_LABEL_FULL = {
  1: 'Externo (rastreável)',
  2: 'Externo (não rastreável)',
  3: 'Interno (rastreável)',
  4: 'Interno (não rastreável)',
};

const TIPO_EXTERNO_RASTREAVEL = TIPO_LABEL_FULL[1];

function displayTipoTransporte(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  if (/externo|interno/i.test(s)) return s;
  const n = Number(s);
  if (Number.isFinite(n) && TIPO_LABEL_FULL[n]) return TIPO_LABEL_FULL[n];
  return s || null;
}

const TRANSSP_GROUPS = [
  {
    key: 'tipo',
    label: 'Tipo de transporte',
    desc: 'Somente tipo de transporte definido',
    color: 'var(--ci2-blue, #265ED4)',
  },
  {
    key: 'integracao',
    label: 'Integração de rastreio',
    desc: 'Somente integração de rastreio vinculada',
    color: 'var(--ci2-teal, #0E8A82)',
  },
  {
    key: 'ambos',
    label: 'Ambos',
    desc: 'Tipo de transporte + integração de rastreio',
    color: 'var(--ci2-violet, #6E56CF)',
  },
  {
    key: 'nao',
    label: 'Não habilitados',
    desc: 'Ainda não configurados',
    color: 'var(--ci2-gray, #767889)',
  },
];

function categorizeTransp(row) {
  const hasTipo = Boolean(displayTipoTransporte(row?.tipo_transp));
  const integs = Array.isArray(row?.integracoes_rastreio) ? row.integracoes_rastreio : [];
  const hasInteg = integs.length > 0;
  if (hasTipo && hasInteg) return 'ambos';
  if (hasTipo) return 'tipo';
  if (hasInteg) return 'integracao';
  return 'nao';
}

export default function TransportadorasCustomersView() {
  const [search, setSearch] = useState('');
  const [searchDeb, setSearchDeb] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);
  const integWrapRef = useRef(null);
  const [integOpenCod, setIntegOpenCod] = useState(null);
  const [integCatalog, setIntegCatalog] = useState([]);
  const [integCatalogReady, setIntegCatalogReady] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const j = await apiVc('/transportadoras/integracoes-logistica');
        if (alive) setIntegCatalog(Array.isArray(j.data) ? j.data : []);
      } catch {
        if (alive) setIntegCatalog([]);
      } finally {
        if (alive) setIntegCatalogReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchDeb(search), 280);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const q = encodeURIComponent(searchDeb.trim());
      const j = await apiVc(`/transportadoras/lista?q=${q}`);
      setRows(Array.isArray(j.data) ? j.data : []);
      if (silent) setError(null);
    } catch (e) {
      if (!silent) {
        setRows([]);
        setError(e.message || 'Falha ao carregar');
      } else {
        setError(e.message || 'Falha ao atualizar a lista');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [searchDeb]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  useEffect(() => {
    if (!integOpenCod) return undefined;
    const onDoc = (e) => {
      if (!integWrapRef.current?.contains(e.target)) setIntegOpenCod(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [integOpenCod]);

  const toggleRow = useCallback((codigo) => {
    const c = String(codigo || '').trim();
    if (!c) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }, []);

  const toggleSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const sortedRows = useMemo(() => {
    const data = [...rows];
    const { key, direction } = sortConfig;
    const mult = direction === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      let av;
      let bv;
      if (key === 'codigo') {
        av = String(a.codigo ?? '').trim();
        bv = String(b.codigo ?? '').trim();
      } else if (key === 'nome') {
        av = String(a.nome ?? '').trim().toLowerCase();
        bv = String(b.nome ?? '').trim().toLowerCase();
      } else {
        av = String(a.estado ?? '').trim().toLowerCase();
        bv = String(b.estado ?? '').trim().toLowerCase();
      }
      return av.localeCompare(bv, 'pt-BR', { numeric: true }) * mult;
    });
    return data;
  }, [rows, sortConfig]);

  const groupedRows = useMemo(() => {
    const map = { tipo: [], integracao: [], ambos: [], nao: [] };
    sortedRows.forEach((r) => {
      map[categorizeTransp(r)].push(r);
    });
    return map;
  }, [sortedRows]);

  const [collapsedGroups, setCollapsedGroups] = useState({ nao: true });

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const selectedList = useMemo(() => [...selected], [selected]);

  const onEscolherTipo = useCallback(
    async (tipoTransp) => {
      if (!selectedList.length || saving) return;
      setSaving(true);
      try {
        await apiVc('/transportadoras/definir-tipo', {
          method: 'POST',
          body: { codigos: selectedList, tipoTransp },
        });
        setMenuOpen(false);
        setSelected(new Set());
        setIntegOpenCod(null);
        await load({ silent: true });
      } catch (e) {
        setError(e.message || 'Falha ao gravar');
      } finally {
        setSaving(false);
      }
    },
    [selectedList, saving, load]
  );

  const openIntegDropdown = useCallback(
    (e, cod) => {
      e.stopPropagation();
      if (integOpenCod === cod) {
        setIntegOpenCod(null);
        return;
      }
      setIntegOpenCod(cod);
    },
    [integOpenCod]
  );

  const applyIntegracao = useCallback(
    async (e, cod, idIntegracao) => {
      e.stopPropagation();
      try {
        await apiVc('/transportadoras/integracao-rastreio', {
          method: 'PATCH',
          body: { codigo: cod, idIntegracao },
        });
        setIntegOpenCod(null);
        await load({ silent: true });
      } catch (err) {
        setError(err.message || 'Falha ao vincular integração');
      }
    },
    [load]
  );

  const unlinkIntegracao = useCallback(
    async (e, cod, idLink) => {
      e.stopPropagation();
      if (!window.confirm('Desvincular esta integração de rastreio desta transportadora?')) {
        return;
      }
      try {
        const q = encodeURIComponent(String(cod || '').trim());
        const lid = encodeURIComponent(String(idLink));
        await apiVc(`/transportadoras/integracao-rastreio?codigo=${q}&idLink=${lid}`, { method: 'DELETE' });
        setIntegOpenCod(null);
        await load({ silent: true });
      } catch (err) {
        setError(err.message || 'Falha ao desvincular integração');
      }
    },
    [load]
  );

  const hasSelection = selected.size > 0;
  const actionDisabled = !hasSelection || saving || loading;

  useEffect(() => {
    if (!hasSelection) setMenuOpen(false);
  }, [hasSelection]);

  return (
    <div className="pcs2-page transsp-page">
      <section className="pcs2-section transsp-section">
        <div className="pcs2-section-aside">
          <div className="pcs2-section-heading">
            <IconTruck size={16} stroke={1.75} aria-hidden />
            <h3 className="pcs2-section-title">Transportadoras</h3>
          </div>
          <p className="pcs2-section-desc">Cadastro e tipo de transporte</p>
        </div>

        <div className="pcs2-section-body">
          <div className="pcs2-subblock">
            <div className="pcs2-subhead pcs2-subhead--with-action">
              <div className="pcs2-subhead-copy">
                <p className="pcs2-subdesc">
                  Selecione transportadoras e defina o tipo de transporte e a integração de rastreio.
                </p>
              </div>
              <div className="pcs2-subhead-action">
                <div className="transsp-toolbar">
                  <BoardToolbarExpandSearch
                    forceExpanded
                    value={search}
                    onChange={setSearch}
                    placeholder="Buscar código, identificação, nome, estado..."
                    ariaLabel="Buscar transportadoras"
                  />
                  <div className="transsp-tipo-wrap" ref={wrapRef}>
                    <EcClickUpFilterChip
                      label={saving ? 'Salvando…' : 'Definir tipo de transporte'}
                      icon={TransspTipoChipIcon}
                      hasSelection={hasSelection}
                      isOpen={menuOpen}
                      disabled={actionDisabled}
                      showValue={false}
                      ariaLabel="Definir tipo de transporte"
                      onClick={() => {
                        if (actionDisabled) return;
                        setMenuOpen((v) => !v);
                      }}
                    />
                    {menuOpen && hasSelection ? (
                      <div
                        className={TIPO_POPOVER_CLASS}
                        role="dialog"
                        aria-label="Tipo de transporte"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <BoardSearchSimplePickerPanel
                          showSearch={false}
                          searchPlaceholder=""
                          searchTerm=""
                          onSearchChange={() => {}}
                          items={TIPO_OPTIONS}
                          getKey={(item) => String(item.value)}
                          getLabel={(item) => item.label}
                          selectedKeys={[]}
                          isItemDisabled={() => saving}
                          onToggle={(item) => onEscolherTipo(item.value)}
                          emptyMessage="Nenhuma opção"
                          listAriaLabel="Tipo de transporte"
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="users-customers-surface transsp-surface">
          <div className="perm-sup-main perm-sup-main--single">
            <section className="perm-panel perm-panel--equip" aria-label="Lista de transportadoras">
              <div className="perm-sup-table-wrap transsp-table-outer">
                <div className="perm-table-scroll perm-table-scroll--main transsp-table-scroll">
              <table className="perm-table perm-table--sup perm-table--transportadoras">
                <thead>
                  <tr>
                    <th className="perm-col-cod perm-th-sort" onClick={() => toggleSort('codigo')}>
                      Código
                      {sortConfig.key === 'codigo' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                    <th className="transsp-col-nome perm-th-sort" onClick={() => toggleSort('nome')}>
                      Nome
                      {sortConfig.key === 'nome' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                    <th className="transsp-col-ident">Identificação</th>
                    <th className="perm-col-brand perm-th-sort" onClick={() => toggleSort('estado')}>
                      Estado
                      {sortConfig.key === 'estado' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                    <th className="transsp-col-tipo">Tipo de transporte</th>
                    <th className="perm-col-sub">Integração de rastreio</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="perm-empty perm-empty--pad">
                        <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando transportadoras" />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={6} className="perm-empty perm-empty--pad">
                        {error}
                      </td>
                    </tr>
                  ) : !rows.length ? (
                    <tr>
                      <td colSpan={6} className="perm-empty perm-empty--pad">
                        Nenhum registro.
                      </td>
                    </tr>
                  ) : (
                    TRANSSP_GROUPS.map((group) => {
                      const groupRows = groupedRows[group.key] || [];
                      if (!groupRows.length) return null;
                      const collapsed = !!collapsedGroups[group.key];
                      const dotStyle =
                        group.key === 'nao'
                          ? { border: `1.5px dashed ${group.color}`, background: 'transparent' }
                          : { background: group.color };
                      return (
                        <Fragment key={group.key}>
                          <tr
                            className={`transsp-group-row${collapsed ? ' collapsed' : ''}`}
                            onClick={() => toggleGroup(group.key)}
                          >
                            <td colSpan={6}>
                              <div className="transsp-group-label">
                                <span className="transsp-chev" aria-hidden>
                                  <ChevronDown size={13} strokeWidth={2.5} />
                                </span>
                                <span className="transsp-dot" style={dotStyle} />
                                {group.label}
                                <span className="transsp-group-count">· {groupRows.length}</span>
                                <span className="transsp-group-desc">{group.desc}</span>
                              </div>
                            </td>
                          </tr>
                          {groupRows.map((r) => {
                      const cod = String(r.codigo || '').trim();
                      const rowSel = selected.has(cod);
                      const tipoLabel = displayTipoTransporte(r.tipo_transp);
                      const integs = Array.isArray(r.integracoes_rastreio) ? r.integracoes_rastreio : [];
                      const linkedNames = new Set(integs.map((x) => String(x.nome || '').trim()).filter(Boolean));
                      const catalogFiltered = integCatalog.filter((it) => !linkedNames.has(it.integracao));
                      return (
                      <tr
                        key={cod}
                        role="row"
                        aria-selected={rowSel}
                        className={`perm-tr perm-tr--model${rowSel ? ' perm-tr--sel' : ''}${collapsed ? ' transsp-row--hidden' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleRow(cod)}
                      >
                        <td className="perm-td-cod">{r.codigo}</td>
                        <td className="perm-td-nome transsp-td-nome" title={r.nome || ''}>
                          {r.nome || '—'}
                        </td>
                        <td className="perm-td-nome transsp-td-ident" title={maskCpfCnpjDisplay(r.cgc_transp)}>
                          {maskCpfCnpjDisplay(r.cgc_transp)}
                        </td>
                        <td className="perm-td-nome">{r.estado || '—'}</td>
                        <td className="perm-td-nome transsp-td-tipo">
                          {tipoLabel ? (
                            <span className="transsp-tipo-badge">{tipoLabel}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td
                          className="perm-td-nome transsp-td-integ"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {tipoLabel === TIPO_EXTERNO_RASTREAVEL ? (
                            <div
                              className="transsp-int-cell"
                              ref={integOpenCod === cod ? integWrapRef : null}
                            >
                              <div className="transsp-int-chips">
                                {integs.map((it) => (
                                  <span key={it.id} className="transsp-int-chip">
                                    <span className="transsp-tipo-badge" title={it.nome}>
                                      {it.nome}
                                    </span>
                                    <button
                                      type="button"
                                      className="transsp-int-unlink"
                                      title="Desvincular"
                                      aria-label={`Desvincular ${it.nome}`}
                                      onClick={(e) => unlinkIntegracao(e, cod, it.id)}
                                    >
                                      <X size={12} strokeWidth={2.5} aria-hidden />
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <button
                                type="button"
                                className="transsp-int-btn"
                                onClick={(e) => openIntegDropdown(e, cod)}
                              >
                                <Plus size={10} strokeWidth={2.5} aria-hidden />
                                Integração
                              </button>
                              {integOpenCod === cod ? (
                                <div className="transsp-int-popover" role="listbox" aria-label="Integrações">
                                  {!integCatalogReady ? (
                                    <div className="transsp-int-popover-msg">Carregando…</div>
                                  ) : catalogFiltered.length ? (
                                    catalogFiltered.map((it) => (
                                      <button
                                        key={it.id}
                                        type="button"
                                        role="option"
                                        className="transsp-int-option"
                                        onClick={(e) => applyIntegracao(e, cod, it.id)}
                                      >
                                        {it.integracao}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="transsp-int-popover-msg">Nenhuma integração disponível</div>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          ) : integs.length ? (
                            <div className="transsp-int-chips transsp-int-chips--readonly">
                              {integs.map((it) => (
                                <span key={it.id} className="transsp-int-chip">
                                  <span className="transsp-tipo-badge">{it.nome}</span>
                                  <button
                                    type="button"
                                    className="transsp-int-unlink"
                                    title="Desvincular"
                                    aria-label={`Desvincular ${it.nome}`}
                                    onClick={(e) => unlinkIntegracao(e, cod, it.id)}
                                  >
                                    <X size={12} strokeWidth={2.5} aria-hidden />
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                      );
                          })}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}
